// ============================================================================
// STATISTIQUES › STOCK › REPORTING STOCK — le stock et ses mouvements sur les niveaux que l'on choisit : chaque cellule
// porte la quantité puis sa valeur, et la ligne de total reste sous les yeux. Réf. maquette du back-office (écran `rst`,
// « Statistiques · Reporting stock ») et docs/CADRAGE-Statistiques-Stock.md §11.
//
// ⚠️ L'ÉCRAN TRIE ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : flux, stock de fin, valeur au PMP, parts, taux et écarts
//    viennent du serveur (`sales/stats-stock-report.ts`). Le squelette du tableau est celui de tous les reportings.
// ⚠️ LE STOCK EST CELUI DE LA FIN DE LA PÉRIODE, ET IL SE COMPARE (décision utilisateur) : la maquette montre le stock
//    du jour, le même dans les deux fenêtres, et éteint ses colonnes en vue Écart ; ici chaque fenêtre lit le sien.
// ⚠️ UNE VALEUR NON MESURABLE S'AFFICHE « — », JAMAIS ZÉRO : une déclinaison en stock sans coût rend le lot inconnu.
// ============================================================================
import type { ReactNode } from 'react';
import { Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { SalesSummaryFilterKey, StockReportMeasures, StockReportMetric, StockReportValues, StockReportView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, dimLabel } from './stats-bar';
import { csvQty, csvRate, dTx, eur0, pc, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, type GridCell, type StatsSort } from './stats-grid';
import type { ReportDisplay } from './stats-reporting';

// ─── Les colonnes ────────────────────────────────────────────────────────────────────────────────

/** Le tri d'ouverture : l'ordre des niveaux, à égalité la plus grosse valeur de stock d'abord. */
export const STOCK_REPORT_DEFAULT_SORT: StatsSort = { k: 'stockQty', d: -1, man: false };

type Block = 'stock' | 'share';
const BLOCKS: Record<Block, string> = { stock: 'var(--blue)', share: 'var(--ink-500)' };

interface StockReportColumn {
  k: StockReportMetric;
  label: Translatable;
  /** La valeur que la cellule porte sous sa quantité — les trois colonnes de stock. */
  value?: 'entriesValueCents' | 'exitsValueCents' | 'stockValueCents';
  unit: 'qty' | 'rate';
  /** La largeur minimale de la maquette ; la colonne s'élargit si son libellé ne tient pas. */
  width: number;
  block: Block;
}

/**
 * LES CINQ COLONNES DU DESSIN, toutes affichées : la maquette n'offre pas de bouton « Colonnes » ici — un tableau de cinq
 * colonnes n'a rien à masquer.
 */
const COLUMNS: StockReportColumn[] = [
  { k: 'entriesQty', label: key('Entrées'), value: 'entriesValueCents', unit: 'qty', width: 116, block: 'stock' },
  { k: 'exitsQty', label: key('Sorties'), value: 'exitsValueCents', unit: 'qty', width: 116, block: 'stock' },
  { k: 'stockQty', label: key('Stock'), value: 'stockValueCents', unit: 'qty', width: 116, block: 'stock' },
  { k: 'shareStockBp', label: key('Répartition stock'), unit: 'rate', width: 116, block: 'share' },
  { k: 'sellThroughBp', label: key('Taux de sortie'), unit: 'rate', width: 104, block: 'stock' },
];
/** Les colonnes que l'onglet sait trier — une vue enregistrée n'en reprend pas d'autre. */
export const STOCK_REPORT_SORT_KEYS: readonly string[] = COLUMNS.map((c) => c.k);

/** La légende de la ligne LIGNES : une cellule, deux lectures. */
export const stockReportLegend = (): { label: string; color: string }[] =>
  [{ label: t('Quantité puis valeur {symbole} HT', { symbole: currentCurrency().symbol }), color: BLOCKS.stock }];

// ─── Le tri ──────────────────────────────────────────────────────────────────────────────────────

type Row = StockReportView['rows'][number];

/** Les indicateurs d'une fenêtre — `null` quand la fenêtre n'est pas lue (pas de comparaison). */
const windowOf = (v: StockReportValues, display: ReportDisplay): StockReportMeasures | null => (display === 'b' ? v.b : v.a);

/** La valeur d'une ligne pour une colonne et un affichage — `null` quand elle ne se mesure pas. */
function cellValue(v: StockReportValues, k: StockReportMetric, display: ReportDisplay): number | null {
  if (display === 'd') return v.growth?.[k] ?? null;
  return windowOf(v, display)?.[k] ?? null;
}

/** À égalité de niveaux, la plus grosse valeur de stock d'abord ; une valeur inconnue après toutes les autres. */
const byStockValue = (x: Row, y: Row): number => {
  const a = x.a.stockValueCents;
  const b = y.a.stockValueCents;
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  return b - a;
};

/** Les lignes dans l'ordre demandé. */
export function sortStockReportRows(rows: Row[], axes: SalesSummaryFilterKey[], sort: StatsSort, display: ReportDisplay, lang: string): Row[] {
  return sortStatsRows(rows, axes.length, sort, (r, k) => cellValue(r, k as StockReportMetric, display), byStockValue, lang);
}

// ─── Les cellules ────────────────────────────────────────────────────────────────────────────────

const UNKNOWN: GridCell = { text: '—', fg: 'var(--ink-300)', fw: 600 };

/**
 * Le texte d'une cellule : en Période et en Comparaison, la quantité puis sa valeur ; en Écart, l'écart relatif seul,
 * vert au-dessus de +0,5 %, rose sous −0,5 % (les seuils de la maquette).
 */
function cell(v: StockReportValues, c: StockReportColumn, display: ReportDisplay): GridCell {
  if (display === 'd') {
    const g = cellValue(v, c.k, 'd');
    if (g === null) return UNKNOWN;
    const flat = Math.abs(g) < 50;
    return { text: dTx(g), fg: flat ? 'var(--ink-500)' : g > 0 ? 'var(--teal-800)' : 'var(--rose-fg)', fw: 700 };
  }
  const src = windowOf(v, display);
  if (!src) return UNKNOWN;
  const x = src[c.k];
  const main: GridCell = {
    text: c.unit === 'rate' ? pc(x) : qty(x), fg: x === null ? 'var(--ink-300)' : 'var(--ink-900)', fw: 700,
  };
  return c.value ? { ...main, sub: { text: eur0(src[c.value]), fg: 'var(--ink-500)' } } : main;
}

const columnLabel = (c: StockReportColumn): string => t(c.label);

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/**
 * LE TABLEAU DU REPORTING STOCK — ses colonnes et ses cellules empilées, dans le squelette commun des reportings.
 * `filtered` dit si un filtre est posé : l'état vide ne dit pas la même chose avec ou sans.
 */
export function StockReportTable({ view, display, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: StockReportView;
  display: ReportDisplay;
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const rows = sortStockReportRows(view.rows, axes, sort, shownDisplay, lang);
  const axisDim = (k: SalesSummaryFilterKey) => FILTER_DIMS.find((d) => d.k === k)!;
  const totalSub = [
    ...(shownDisplay === 'd' ? [t('écart A/B')] : shownDisplay === 'b' ? [t('période B')] : []),
    t('{n} ligne', { n: view.rows.length }),
    ...(sort.man ? [] : [t('ordre de la ventilation')]),
  ].join(' · ');
  const strong = (x: ReactNode) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>;

  return (
    <StatsGrid label={t('Reporting stock')} sort={sort} onSort={onSort} busy={busy} stacked
      axes={axes.map((k) => ({ label: dimLabel(axisDim(k)), code: k === 'art' }))}
      columns={COLUMNS.map((c) => ({ k: c.k, label: columnLabel(c), width: c.width, color: BLOCKS[c.block] }))}
      total={{ sub: totalSub, cells: COLUMNS.map((c) => cell(view.total, c, shownDisplay)) }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: r.parts, sub: t('{n} article', { n: r.articles }),
        cells: COLUMNS.map((c) => cell(r, c, shownDisplay)),
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Ni stock ni mouvement sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre période, ou comparez-la à une autre.')}</div>
                </>
              )}
            </div>
          )}
          <div style={{
            padding: '11px 14px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', borderRadius: '0 0 var(--r-sm) var(--r-sm)',
            fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.6,
          }}>
            <Trans k='<1>Chaque cellule porte la quantité puis sa valeur {symbole} HT</1>, au PMP de la date : deux lectures d’un même fait. Le stock est celui de la <2>fin de la période</2> ; les entrées et les sorties comptent tous les mouvements, si bien que le stock d’ouverture plus les entrées moins les sorties redonne le stock. Le taux de sortie se recalcule sur les sommes — vendu / (vendu + stock) —, jamais en moyennant des pourcentages. En vue <3>Écart</3>, chaque période lit son propre stock de fin : le stock se compare comme les flux. Une valeur non mesurable affiche « — », jamais zéro.'
              vars={{ symbole: currentCurrency().symbol }}
              parts={[strong, strong, strong]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, les cinq colonnes — la quantité et sa valeur dans deux colonnes du
 * fichier —, le tri, la ligne de total.
 * ⚠️ UN NOMBRE INCONNU RESTE VIDE dans le fichier — un 0 dans un tableur devient une somme fausse.
 * ⚠️ EN ÉCART, LE FICHIER PORTE CE QUE L'ÉCRAN MONTRE : l'écart des quantités et des taux, pas celui des valeurs.
 */
export function exportStockReport(view: StockReportView, display: ReportDisplay, sort: StatsSort, lang: string): void {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const currency = currentCurrency().code;
  // Une colonne du fichier : son en-tête et sa valeur pour une ligne.
  const fileColumns: { head: string; value: (v: StockReportValues) => string }[] = COLUMNS.flatMap((c) => {
    if (shownDisplay === 'd') return [{ head: `${columnLabel(c)} (%)`, value: (v: StockReportValues) => csvRate(cellValue(v, c.k, 'd')) }];
    const main = {
      head: c.unit === 'rate' ? `${columnLabel(c)} (%)` : columnLabel(c),
      value: (v: StockReportValues) => {
        const x = windowOf(v, shownDisplay)?.[c.k] ?? null;
        return c.unit === 'rate' ? csvRate(x) : x === null ? '' : csvQty(x);
      },
    };
    const valueKey = c.value;
    if (!valueKey) return [main];
    return [main, {
      head: t('{v} — valeur ({devise})', { v: columnLabel(c), devise: currency }),
      value: (v: StockReportValues) => {
        const x = windowOf(v, shownDisplay)?.[valueKey] ?? null;
        return x === null ? '' : toInputValue(x);
      },
    }];
  });
  // ⚠️ LE FICHIER DIT CE QU'IL CONTIENT : une comparaison ou un écart, intitulés comme la période, en seraient
  //    indiscernables une fois ouverts.
  const head = (h: string) => (shownDisplay === 'b' ? t('{v} — période B', { v: h }) : shownDisplay === 'd' ? t('{v} — écart A/B', { v: h }) : h);
  const ref = view.reference;
  const fileName = shownDisplay === 'b' && ref ? `reporting-stock-comparaison-${ref.from}-${ref.to}`
    : shownDisplay === 'd' && ref ? `reporting-stock-ecart-${view.period.from}-${view.period.to}-vs-${ref.from}-${ref.to}`
      : `reporting-stock-${view.period.from}-${view.period.to}`;
  const rows = sortStockReportRows(view.rows, axes, sort, shownDisplay, lang);
  exporterCsv(fileName,
    [...axes.map((k) => dimLabel(FILTER_DIMS.find((d) => d.k === k)!)), t('Articles'), ...fileColumns.map((c) => head(c.head))],
    [
      [t('TOTAL'), ...axes.slice(1).map(() => ''), String(view.scope.references), ...fileColumns.map((c) => c.value(view.total))],
      ...rows.map((r) => [
        ...r.parts.map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.articles),
        ...fileColumns.map((c) => c.value(r)),
      ]),
    ]);
}
